import { Component, input, contentChild, computed, inject, OnInit, OnDestroy, AfterViewInit, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { SidebarService } from '../../data-access/sidebar.service';
import { TemplateRef } from '@angular/core';

@Component({
  selector: 'sidebar-item',
  standalone: true,
  imports: [CommonModule],
  template: `
    <!-- This component doesn't render anything directly -->
    <!-- It's used for configuration and content projection -->
  `
})
export class SidebarItemComponent implements OnInit, AfterViewInit, OnDestroy {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private sidebarService = inject(SidebarService);

  routePath = input<string | undefined>(undefined); // Optional for route-based items
  id = input<string | undefined>(undefined); // Optional for component-based items
  title = input.required<string>();
  className = input.required<string>();
  position = input.required<number>();
  disabled = input<boolean>(false);

  sidebarIconTemplate = contentChild<TemplateRef<any>>('sidebarIcon');
  headerTemplate = contentChild<TemplateRef<any>>('header');
  contentTemplate = contentChild<TemplateRef<any>>('content');

  // Helper computed to determine if this is a route-based item
  isRouteBased = computed(() => !!this.routePath());

  // Helper computed to determine if this is a component-based item
  isComponentBased = computed(() => !!this.id());

  // Track if this item is active
  isActive = computed(() => {
    if (this.isRouteBased()) {
      return this.router.url.includes(this.routePath()!);
    } else if (this.isComponentBased()) {
      return this.sidebarService.isComponentActive(this.id()!);
    }
    return false;
  });

  // Effect to watch for changes in disabled state and update the sidebar service
  private disabledEffect = effect(() => {
    if (this.isComponentBased()) {
      const id = this.id();
      if (id) {
        this.sidebarService.updateComponentItem(id, {
          disabled: this.disabled()
        });
      }
    }
  });

  ngOnInit(): void {
    // Register this item with the sidebar service
    if (this.isComponentBased()) {
      this.sidebarService.addComponentItem({
        id: this.id()!,
        title: this.title(),
        className: this.className(),
        active: false,
        disabled: this.disabled(),
        position: this.position(),
        component: this.createDynamicComponent(),
        componentInputs: {}
      });
    }
  }

  ngAfterViewInit(): void {
    // Update the component item with templates after view init
    if (this.isComponentBased()) {
      this.sidebarService.updateComponentItem(this.id()!, {
        sidebarIconTemplate: this.sidebarIconTemplate(),
        headerTemplate: this.headerTemplate(),
        contentTemplate: this.contentTemplate()
      });
    }
  }

  ngOnDestroy(): void {
    // Clean up when component is destroyed
    if (this.isComponentBased()) {
      this.sidebarService.removeComponentItem(this.id()!);
    }
  }

  // Method to handle item click
  handleClick(): void {
    if (this.disabled()) return;

    if (this.isRouteBased()) {
      this.handleRouteClick();
    } else if (this.isComponentBased()) {
      this.handleComponentClick();
    }
  }

  private handleRouteClick(): void {
    const currentRoute = this.routePath();
    if (!currentRoute) return;

    // Navigate to the route
    this.router.navigate([currentRoute], { relativeTo: this.activatedRoute });
  }

  private handleComponentClick(): void {
    const currentId = this.id();
    if (!currentId) return;

    // Toggle the component in the sidebar service
    this.sidebarService.toggleComponentItem(currentId);
  }

  private createDynamicComponent(): any {
    const contentTemplate = this.contentTemplate();
    // Create a dynamic component that renders the content template
    return class DynamicComponent {
      static __annotations__ = [{
        selector: 'dynamic-component',
        template: '<ng-container *ngTemplateOutlet="contentTemplate"></ng-container>'
      }];
      
      contentTemplate = contentTemplate;
    };
  }

}
